import { useRef, useEffect, useCallback } from "react";
import { config } from '#/config';
import { useAudioPlayback } from '#/contexts/PlaybackContext';
import { useRecording } from "#/contexts/RecordingContext";
import silenceAudio from '#/assets/sounds/silence.mp3';
import logger from "#/utils/logger";
import { RecordingMode } from "#/types/preferences";

interface AudioPlaybackManagerProps {
  audioOutputFileName?: string;
  isMuted?: boolean;
}

export const AudioPlaybackManager = ({ audioOutputFileName, 
                                       isMuted }: AudioPlaybackManagerProps) => {
  const { initPlayback, audioUnlocked, setAudioUnlocked } = useAudioPlayback();
  const { isRecording, cancelRecording, pauseRecording, resumeRecording, recordingMode } = useRecording();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (audioOutputFileName && !isMuted) {
      if (audioRef.current) {
        if (isRecording) {
          if (recordingMode == RecordingMode.Automatic) {
            logger.debug("Pausing recording while starting to play audio")
            pauseRecording();
          } else {
            logger.debug("Cancelling recording while starting to play audio")
            cancelRecording();
          }
        }
        logger.debug("Setting current output file to " + audioOutputFileName);
        audioRef.current.src = `${config.backendUrl}/audio_file?file_name=${audioOutputFileName}`;
        audioRef.current.play().then(() => {logger.debug("Audio playing started")})
                        .catch(err => logger.error("Audio playing failed: ", err));
      }
    }
  }, [audioOutputFileName, isMuted]);

  // Audio unlocking
  const addAudioUnlockHook = useCallback((audioElement: HTMLAudioElement) => {
    logger.debug("Mounting audio element");
    audioRef.current = audioElement;

    const unlockAudio = (_: Event) => {
      logger.debug("Unlocking audio...");
      initPlayback(audioElement);
      audioElement.src = silenceAudio;
      audioElement.muted = false;
      audioElement.volume = 1;

      audioElement.addEventListener('ended', (_: Event) => {
          logger.debug("Audio unlocking ended");
          setAudioUnlocked(true);
        })
      audioElement.addEventListener('error', (_: Event) => {
          logger.debug("Audio unlocking error: ", audioElement.error?.message);
        })
      audioElement.play()
        .then(() => {
          logger.debug("Audio unlocking started");
        })
        .catch((err) => logger.error("Audio unlock failed:", err));
    };

    if (audioUnlocked) {
      // Initialize audio element for audio
      initPlayback(audioElement);
      return () => {
        logger.debug("Unmounting audio element");
        audioRef.current = null;
      };
    } else {
      logger.debug("Not unlocked - adding event listeners for audio unlocking")
      document.addEventListener('mouseup', unlockAudio, { once: true });
      document.addEventListener('touchend', unlockAudio, { once: true });
      return () => {
        logger.debug("Unmounting audio element");
        document.removeEventListener('mouseup', unlockAudio);
        document.removeEventListener('touchend', unlockAudio);
        audioRef.current = null;
      };
    }
    // Unreachable - already returned
  }, []);

  const playingCeased = useCallback(async () => {
    // Playing ceased for now - resume recording
    logger.debug("Playing ceased - resuming recording");
    await resumeRecording();
  }, [recordingMode])

  return (
    <audio ref={addAudioUnlockHook} crossOrigin="anonymous"
           onPause={playingCeased} onEnded={playingCeased}/>
  );
};
